import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router-dom";
import BrandLogo from "@/components/brand/BrandLogo";
import { NAV_ICONS } from "@/config/navIcons";
import {
  NAV_GROUP_LABELS,
  NAV_GROUP_ORDER,
  appNavigation,
} from "@/config/navigation";
import { navAllowedForRole } from "@/domain/access";
import { canAccessCustomerNavItem } from "@/domain/permissions";
import { navAccessMeta } from "@/config/navAccess";
import { useAccess } from "@/hooks/useAccess";
import { useSessionStore } from "@/store/session.store";
import { primaryTintBg } from "@/theme/surfaces";

const subheaderSx = {
  lineHeight: "28px",
  fontSize: "0.7rem",
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: "text.disabled",
  bgcolor: "transparent",
  mt: 1,
  px: 1,
};

const navItemSx = (collapsed) => ({
  borderRadius: 2,
  mb: 0.5,
  py: 0.9,
  mx: 1,
  px: collapsed ? 1 : 1.5,
  justifyContent: collapsed ? "center" : "flex-start",
  color: "text.secondary",
  "&.Mui-selected": {
    color: "primary.main",
    bgcolor: (th) => primaryTintBg(th, true),
    fontWeight: 700,
    "& .MuiListItemIcon-root": { color: "primary.main" },
  },
  "&:hover": {
    bgcolor: (th) =>
      th.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.05)"
        : "rgba(0, 0, 0, 0.04)",
  },
});

function Sidebar({ collapsed = false, onToggleCollapsed, onNavigate }) {
  const { t } = useTranslation();
  const user = useSessionStore((s) => s.user);
  const { modules, role, isGroupAdmin, entitlements, permissions } = useAccess();
  const location = useLocation();
  const visible = appNavigation.filter((item) => {
    if (!navAllowedForRole(role, item.name)) return false;
    if (!modules.includes(item.module)) return false;
    if (item.roles && !item.roles.includes(role)) return false;
    const meta = navAccessMeta(item.href);
    const enriched = { ...item, ...meta };
    return canAccessCustomerNavItem(enriched, {
      role,
      modules: entitlements,
      permissions,
    });
  });
  const renderNavItem = (item) => {
    const active =
      location.pathname === item.href ||
      location.pathname.startsWith(`${item.href}/`);
    const Icon = NAV_ICONS[item.iconKey];
    const button = (
      <ListItemButton
        key={item.href}
        component={NavLink}
        to={item.href}
        onClick={onNavigate}
        selected={active}
        sx={navItemSx(collapsed)}
      >
        <ListItemIcon
          sx={{
            minWidth: collapsed ? 0 : 36,
            color: "inherit",
            justifyContent: "center",
          }}
        >
          {Icon ? <Icon fontSize="small" /> : null}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={t(item.navId, { defaultValue: item.name })}
            slotProps={{
              primary: { sx: { fontSize: "0.86rem", fontWeight: 600 } },
            }}
          />
        )}
      </ListItemButton>
    );
    if (collapsed) {
      return (
        <Tooltip
          key={item.href}
          title={t(item.navId, { defaultValue: item.name })}
          placement="right"
        >
          <Box component="span" sx={{ display: "block" }}>
            {button}
          </Box>
        </Tooltip>
      );
    }
    return button;
  };
  const renderGroup = (groupId) => {
    const items = visible.filter((i) => i.group === groupId);
    if (items.length === 0) return null;
    const label =
      groupId === "top"
        ? null
        : t(`navGroups.${groupId}`, {
            defaultValue: NAV_GROUP_LABELS[groupId],
          });
    return (
      <List key={groupId} dense disablePadding>
        {label &&
          (collapsed ? (
            <Divider sx={{ my: 1.5, mx: 1 }} />
          ) : (
            <ListSubheader component="div" sx={subheaderSx}>
              {label}
            </ListSubheader>
          ))}
        {items.map(renderNavItem)}
      </List>
    );
  };
  return (
    <Box
      component="nav"
      aria-label="Main navigation"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          gap: 1,
          px: collapsed ? 1 : 2,
          pt: 0.5,
          pb: 1.5,
          minHeight: 52,
        }}
      >
        <BrandLogo compact={collapsed} />
        {onToggleCollapsed && (
          <IconButton
            size="small"
            onClick={onToggleCollapsed}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            sx={{
              display: { xs: "none", md: "inline-flex" },
              color: "text.secondary",
              bgcolor: "action.hover",
              borderRadius: 1.5,
              p: 0.8,
              "&:hover": { bgcolor: "action.selected", color: "primary.main" },
            }}
          >
            {collapsed ? (
              <ChevronRightIcon fontSize="small" />
            ) : (
              <ChevronLeftIcon fontSize="small" />
            )}
          </IconButton>
        )}
      </Box>
      <Divider sx={{ my: 1 }} />
      <Box sx={{ flex: 1, overflowY: "auto", py: 0.5 }}>
        {NAV_GROUP_ORDER.map(renderGroup)}
      </Box>
      <Divider />
      <Box
        sx={{
          p: collapsed ? 1 : 2,
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            bgcolor: (th) => primaryTintBg(th, true),
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {user?.name
            .split(" ")
            .map((p) => p[0])
            .join("")
            .slice(0, 2)}
        </Box>
        {!collapsed && (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
              {user?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {isGroupAdmin ? "Group admin" : role}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
export { Sidebar as default };
